import { useEffect, useState } from 'react'

import {
  ContractsRepository,
  DocumentModel,
  ProductConditionsModel,
} from '@/Contract/domain'
import { textToPdf } from '@/Shared/domain'
import { useMetaResponseHandler } from '@/Shared/ui/form'
import { useLoader } from '@/Shared/ui/loader'

export interface IUseProductsDataController {
  model: ProductConditionsModel[]
  downloadContracts: (model: ProductConditionsModel) => Promise<void>
}

export const useProductsDataController = (
  id: number,
  repository: ContractsRepository,
  products: number[],
): IUseProductsDataController => {
  const { startLoading, stopLoading } = useLoader()
  const { handleMetaResponse } = useMetaResponseHandler()

  const [model, setModel] = useState<ProductConditionsModel[]>([])

  const load = async (productId: number): Promise<ProductConditionsModel | undefined> => {
    startLoading()

    const response = await repository.GetProductConditions(id, productId)

    stopLoading()

    if (handleMetaResponse(response.meta, undefined, { notifySuccess: false })) {
      return response.data as ProductConditionsModel
    }
  }

  const loadAllProducts = async (productIds: number[]): Promise<void> => {
    startLoading()

    const productsConditions: ProductConditionsModel[] = []

    for (const productId of productIds) {
      const product = await load(productId)
      if (product) {
        productsConditions.push(product)
      }
    }

    setModel(productsConditions)

    stopLoading()
  }

  const getContractsToDownload = async (
    model: ProductConditionsModel,
  ): Promise<DocumentModel[]> => {
    startLoading()

    const response = await repository.GetProductContracts(id, model.id)

    stopLoading()

    if (handleMetaResponse(response.meta, undefined, { notifySuccess: false })) {
      return response.data as DocumentModel[]
    }

    return [] as DocumentModel[]
  }

  const downloadContracts = async (model: ProductConditionsModel): Promise<void> => {
    const contracts = await getContractsToDownload(model)

    contracts.forEach(contract => {
      textToPdf(
        contract.docFileContent,
        contract.docFileName.split('.').slice(0, -1).join('.'),
      )
    })
  }

  useEffect(() => {
    if (id) {
      products && loadAllProducts(products)
    }
  }, [])

  return {
    model,
    downloadContracts,
  }
}
